import React, { useState, useEffect } from 'react';
import { 
  Factory, 
  Users, 
  ClipboardList, 
  AlertTriangle,
  TrendingUp,
  Eye,
  Plus,
  Filter,
  Download,
  Search,
  Calendar as CalendarIcon,
  BarChart3,
  PieChart,
  Activity,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Settings,
  Bell,
  MapPin,
  Building,
  Shield,
  Target,
  Zap,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import Header from '../../components/common/Header';
import StatCard from '../../components/common/StatCard';
import { Link } from 'react-router-dom';
import { plantAPI } from '../../services/plantAPI';
import { auditAPI } from '../../services/auditAPI';
import { superAdminAPI } from '../../services/superAdminAPI';
import { reportAPI } from '../../services/reportAPI';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, LineElement, PointElement, RadialLinearScale } from 'chart.js';
import { Bar, Doughnut, Line, PolarArea } from 'react-chartjs-2';
import Calendar from 'react-calendar';
import { format, parseISO, isSameDay } from 'date-fns';
import 'react-calendar/dist/Calendar.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, LineElement, PointElement, RadialLinearScale);

const SuperAdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState('overview');
  const [filters, setFilters] = useState({
    status: 'all',
    riskLevel: 'all',
    plantId: 'all'
  });
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, [selectedPeriod]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [analyticsData, plantsData] = await Promise.all([
        superAdminAPI.getDashboardAnalytics(selectedPeriod),
        plantAPI.getAllPlants()
      ]);
      
      setDashboardData(analyticsData);
      setPlants(plantsData.data || plantsData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setRefreshing(false);
  };

  const handleDownloadReport = async (format) => {
    try {
      const response = await reportAPI.generateComprehensiveReport({
        period: selectedPeriod,
        filters,
        includeAllPlants: true
      }, format);
      
      // Validate that the response is a Blob before proceeding
      if (response instanceof Blob) {
        reportAPI.downloadReport(response, `super-admin-dashboard-${format}.${format}`);
      } else {
        // Handle the case where the API returned a non-Blob object (e.g., an error message)
        console.error("The API did not return a valid Blob object.");
        // You could display a user-friendly error message here
      }
    } catch (error) {
      console.error('Error downloading report:', error);
      // Display an error to the user
    }
  };

  const filteredPlants = plants.filter(plant =>
    plant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plant.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getEventsByDate = (date) => {
    if (!dashboardData?.calendarEvents) return [];
    return dashboardData.calendarEvents.filter((event) => 
      isSameDay(parseISO(event.createdAt), date) || 
      (event.dueDate && isSameDay(parseISO(event.dueDate), date))
    );
  };

  const tileContent = ({ date, view }) => {
    if (view === 'month') {
      const events = getEventsByDate(date);
      if (events.length > 0) {
        const hasHighRisk = events.some((e) => e.riskLevel === 'Critical' || e.riskLevel === 'High');
        return (
          <div className="flex justify-center mt-1">
            <div className={`w-2 h-2 rounded-full ${
              hasHighRisk ? 'bg-red-500' : 'bg-blue-500'
            }`}></div>
          </div>
        );
      }
    }
    return null;
  };

  // Chart configurations
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  // Plant Performance Chart
  const plantPerformanceData = {
    labels: dashboardData?.plantPerformance?.slice(0, 10).map((plant) => plant.name) || [],
    datasets: [
      {
        label: 'Compliance Score',
        data: dashboardData?.plantPerformance?.slice(0, 10).map((plant) => plant.complianceScore || 0) || [],
        backgroundColor: 'rgba(59, 130, 246, 0.6)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 2,
      },
      {
        label: 'Active Audits',
        data: dashboardData?.plantPerformance?.slice(0, 10).map((plant) => plant.activeAudits || 0) || [],
        backgroundColor: 'rgba(245, 158, 11, 0.6)',
        borderColor: 'rgba(245, 158, 11, 1)',
        borderWidth: 2,
      }
    ],
  };

  // Risk Distribution Chart
  const riskDistributionData = {
    labels: ['Completed', 'In Progress', 'Pending'],
    datasets: [
      {
        data: [
          dashboardData?.auditStatusStats?.find((r) => r._id === 'Completed')?.count || 0,
          dashboardData?.auditStatusStats?.find((r) => r._id === 'In Progress')?.count || 0,
          dashboardData?.auditStatusStats?.find((r) => r._id === 'Pending')?.count || 0,
        ],
        backgroundColor: [
          'rgba(16, 185, 129, 0.8)',
          'rgba(120, 11, 245, 0.8)',
          'rgba(239, 199, 68, 0.8)',
        ],
        borderColor: [
          'rgba(16, 185, 129, 1)',
          'rgba(120, 11, 245, 1)',
          'rgba(239, 199, 68, 1)',
        ],
        borderWidth: 2,
      },
    ],
  };

  // Monthly Trend Chart
  const monthlyTrendData = {
    labels: dashboardData?.monthlyAuditTrends?.map((trend) => 
      format(new Date(trend._id.year, trend._id.month - 1), 'MMM yyyy')
    ) || [],
    datasets: [
      {
        label: 'Total Audits',
        data: dashboardData?.monthlyAuditTrends?.map((trend) => trend.total) || [],
        borderColor: 'rgba(59, 130, 246, 1)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Completed',
        data: dashboardData?.monthlyAuditTrends?.map((trend) => trend.completed) || [],
        borderColor: 'rgba(16, 185, 129, 1)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        borderWidth: 2,
        fill: false,
        tension: 0.4,
      }
    ]
  };

  // User Distribution Chart
  const userDistributionData = {
    labels: ['Super Admins', 'Admins', 'Employees'],
    datasets: [{
      data: [
        dashboardData?.userDistribution?.find((u) => u._id === 'SuperAdmin')?.count || 0,
        dashboardData?.userDistribution?.find((u) => u._id === 'Admin')?.count || 0,
        dashboardData?.userDistribution?.find((u) => u._id === 'Employee')?.count || 0,
      ],
      backgroundColor: ['#8B5CF6', '#3B82F6', '#10B981'],
      borderColor: ['#8B5CF6', '#3B82F6', '#10B981'],
      borderWidth: 2,
    }],
  };

  // Plant Status Distribution
  const plantStatusData = {
    labels: ['Active', 'Maintenance', 'Inactive'],
    datasets: [{
      data: [
        dashboardData?.plantStatusDistribution?.find((p) => p._id === 'Active')?.count || 0,
        dashboardData?.plantStatusDistribution?.find((p) => p._id === 'Maintenance')?.count || 0,
        dashboardData?.plantStatusDistribution?.find((p) => p._id === 'Inactive')?.count || 0,
      ],
      backgroundColor: ['#10B981', '#F59E0B', '#EF4444'],
      borderColor: ['#10B981', '#F59E0B', '#EF4444'],
      borderWidth: 2,
    }],
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header title="Super Admin Dashboard" />
      
      <main className="p-4 md:p-6 max-w-7xl mx-auto">
        {/* Enhanced Action Bar */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
          <div></div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 w-full lg:w-auto">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-auto"
            >
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
            </select>
            
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors disabled:opacity-50 w-full sm:w-auto"
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
              
            </button>
          </div>
          
          {/* <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 w-full lg:w-auto">
            <button
              onClick={() => handleDownloadReport('pdf')}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors w-full sm:w-auto"
            >
              <Download size={16} />
              <span>PDF</span>
            </button>
            <button
              onClick={() => handleDownloadReport('excel')}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors w-full sm:w-auto"
            >
              <Download size={16} />
              <span>Excel</span>
            </button>
            <button
              onClick={() => handleDownloadReport('word')}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors w-full sm:w-auto"
            >
              <Download size={16} />
              <span>Word</span>
            </button>
          </div> */}
        </div>

        {/* Enhanced Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          <StatCard
            title="Total Plants"
            value={dashboardData?.overview?.totalPlants || 0}
            icon={<Factory size={24} />}
            color="blue"
            
          />
          <StatCard
            title="Active Audits"
            value={dashboardData?.overview?.totalAudits - (dashboardData?.auditStatusStats?.find((s) => s._id === 'Completed')?.count || 0)}
            icon={<ClipboardList size={24} />}
            color="yellow"
           
          />
          <StatCard
            title="Total Users"
            value={dashboardData?.overview?.totalUsers || 0}
            icon={<Users size={24} />}
            color="green"
            
          />
          <StatCard
            title="Total Audits"
            value={dashboardData?.overview?.totalAudits || 0}
            icon={<ClipboardList size={24} />}
            color="purple"
           
          />
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto space-x-4 px-4 sm:px-6">
              {[
                { id: 'overview', name: 'Overview', icon: BarChart3 },
                { id: 'analytics', name: 'Analytics', icon: PieChart },
                { id: 'calendar', name: 'Calendar', icon: CalendarIcon },
                { id: 'plants', name: 'Plants', icon: Factory }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-shrink-0 flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon size={18} />
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Plant Performance</h3>
              </div>
              <div className="h-80">
                <Bar data={plantPerformanceData} options={chartOptions} />
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Risk Distribution</h3>
              </div>
              <div className="h-80">
                <Doughnut data={riskDistributionData} options={chartOptions} />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-8">
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-4 md:p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Monthly Audit Trends</h3>
                
              </div>
              <div className="h-80">
                <Line data={monthlyTrendData} options={chartOptions} />
              </div>
            </div>

            <div className="space-y-4 md:space-y-6">
              <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">User Distribution</h3>
                <div className="h-64">
                  <PolarArea data={userDistributionData} options={chartOptions} />
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Plant Status</h3>
                <div className="h-48">
                  <Doughnut data={plantStatusData} options={chartOptions} />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'calendar' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-8">
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-4 md:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Audit Calendar</h3>
              <div className="calendar-container">
                <Calendar
                  onChange={setSelectedDate}
                  value={selectedDate}
                  tileContent={tileContent}
                  className="w-full border-none"
                />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Events for {format(selectedDate, 'MMM dd, yyyy')}
              </h3>
              <div className="space-y-3">
                {getEventsByDate(selectedDate).map((event) => (
                  <div key={event.id} className="p-3 border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">{event.title}</h4>
                        <p className="text-sm text-gray-600">{event.plantId?.name}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        event.riskLevel === 'Critical' ? 'bg-red-100 text-red-800' :
                        event.riskLevel === 'High' ? 'bg-orange-100 text-orange-800' :
                        event.riskLevel === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {event.riskLevel}
                      </span>
                    </div>
                  </div>
                ))}
                {getEventsByDate(selectedDate).length === 0 && (
                  <p className="text-gray-500 text-center py-4">No events for this date</p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'plants' && (
          <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 md:mb-0">Plants Overview</h3>
              
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 w-full md:w-auto">
                <div className="relative w-full md:w-64">
                  <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search plants..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <Link to="/plants" 
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors w-full sm:w-auto">
                  <Plus size={16} />
                  <span>Add Plant</span>
                </Link>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {filteredPlants.map((plant) => (
                <div key={plant._id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-semibold text-gray-900">{plant.name}</h4>
                      <p className="text-sm text-gray-600 flex items-center">
                        <MapPin size={14} className="mr-1" />
                        {plant.location}
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div>
                      <p className="text-xs text-gray-500">Active Audits</p>
                      <p className="font-semibold text-blue-600">{plant?.totalAudits-plant?.completedAudits || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Employees</p>
                      <p className="font-semibold text-gray-900">{plant.employeeCount || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Completed Audits</p>
                      <p className="font-semibold text-green-600">{plant.completedAudits || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Total Audits</p>
                      <p className="font-semibold text-gray-900">{plant.totalAudits || 0}</p>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      plant.status === 'Active' 
                        ? 'bg-green-100 text-green-800' 
                        : plant.status === 'Maintenance'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {plant.status || 'Active'}
                    </span>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <span className="text-xs text-gray-600">Online</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Activities */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mt-8">
          <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Audits</h3>
            <div className="space-y-3">
              {dashboardData?.recentActivities?.audits?.slice(0, 5).map((audit) => (
                <div key={audit._id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div>
                    <h4 className="font-medium text-gray-900">{audit.title}</h4>
                    <p className="text-sm text-gray-600">
                      {audit.plantId?.name} • {format(parseISO(audit.createdAt), 'MMM dd')}
                    </p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    audit.status === 'Completed' ? 'bg-green-100 text-green-800' :
                    audit.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {audit.status}
                  </span>
                </div>
              )) || []}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Plants</h3>
            <div className="space-y-3">
              {dashboardData?.recentActivities?.plants?.map((plant) => (
                <div key={plant._id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div>
                    <h4 className="font-medium text-gray-900">{plant.name}</h4>
                    <p className="text-sm text-gray-600">
                      {plant.location} • {format(parseISO(plant.createdAt), 'MMM dd')}
                    </p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    plant.status === 'Active' ? 'bg-green-100 text-green-800' :
                    plant.status === 'Maintenance' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {plant.status}
                  </span>
                </div>
              )) || []}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SuperAdminDashboard;